/**
 * useLatestAnalysis — VP-256. Ostatnia zapisana analiza Analityka Marketingowego.
 *
 * Zwykły GET: nie odpytuje Meta/GHL, nie woła LLM, nie zużywa tokenów. Tokeny
 * schodzą wyłącznie przy jawnym przeliczeniu (`/quick-analysis`).
 *
 * Zakres (tryb + filtr platform) jest częścią klucza — analiza Meta i analiza
 * Google to dwa różne wyniki, więc przełączenie filtra nie może pokazać cudzego
 * zakresu jako „ostatniej analizy".
 */

import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";

export type AnalysisFreshness = "fresh" | "aging" | "stale";

export type CachedAnalysis = {
  recommendations: string;
  variant: string | null;
  createdAt: string;
  ageHours: number;
  freshness: AnalysisFreshness;
};

export type AnalysisScope = {
  analysisMode: string;
  platformFilter: string;
};

export function latestAnalysisQueryKey(
  variant: string,
  forUserId: string | undefined,
  scope: AnalysisScope
) {
  return [
    "marketing-analyst-latest-analysis",
    variant,
    forUserId,
    scope.analysisMode,
    scope.platformFilter,
  ] as const;
}

export function useLatestAnalysis(
  variant: string,
  forUserId: string | undefined,
  scope: AnalysisScope
) {
  return useQuery<{ analysis: CachedAnalysis | null }>({
    queryKey: latestAnalysisQueryKey(variant, forUserId, scope),
    queryFn: () => {
      const params = new URLSearchParams({
        variant,
        analysisMode: scope.analysisMode,
        platformFilter: scope.platformFilter,
      });
      if (forUserId) params.set("forUserId", forUserId);
      return apiRequest("GET", `/api/marketing-analyst/latest-analysis?${params.toString()}`);
    },
    staleTime: 60_000,
  });
}
